import type React from 'react';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Category, type GameV1 } from '@/api/games';
import { Button } from '@/components/buttons';
import { ColorMode } from '@/components/GameItem/styled';
import type { ScGameItemPlugProps } from '@/components/GamesGrid/styled';
import { NativeSlider } from '@/components/NativeSlider';
import { SliderArrow } from '@/components/NativeSlider/helpers';
import { SectionTitle } from '@/components/SectionTitle';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { createItems } from '@/helpers/common';
import { getSliderParams } from '@/helpers/getSliderParams';
import { useNavigation } from '@/hooks/navigation';
import { useResponsive } from '@/modules/client';
import { GameCardFactory, ShowAllGameCard } from '@/modules/game/components';
import { sendHotAndColdEvent } from '@/modules/googleAnalytics/events/gamesEvents';
import { useGetCommonStore } from '@/store';
import type { SliderParams } from '../LobbyGamesSlider';
import type { LobbyGamesSlideProps } from '../LobbyGamesSlider/components/Slide/Slide';
import { ScLobbyGamesSlide } from '../LobbyGamesSlider/components/Slide/styled';
import { sortHotAndColdLobby } from './helpers';
import { useHotAndColdGames } from './hooks/useHotAndColdGames';

interface HotAndColdLobbyGamesSliderProps
  extends Pick<ScGameItemPlugProps, 'plugBlack'> {
  sliderParams: SliderParams;
  title: string | React.ReactElement;
  description?: string;
  titleIcon?: string | React.ReactElement;
  titleMultiRow?: boolean;
  darkArrows?: boolean;
  noArrows?: boolean;
  blackShowAllCard?: boolean;
  gameColorMode?: ColorMode;
}

interface ISlideProps extends Omit<LobbyGamesSlideProps, 'games'> {
  games: (GameV1 | { isEmpty: boolean })[];
}

export const Slide: FC<ISlideProps> = ({
  games,
  link,
  rows,
  cols,
  gameColorMode,
}) => {
  if (!games) {
    return null;
  }

  const mode = gameColorMode === ColorMode.GRAY_BG ? 'black' : 'dark';

  const sendGAEvent = (game: GameV1) => {
    sendHotAndColdEvent({
      type: game.hot ? 'hot' : 'cold',
      game_name: game.title,
    });
  };

  return (
    <ScLobbyGamesSlide cols={cols} rows={rows}>
      {games.map((game, index) =>
        'isEmpty' in game ? (
          <GameCardFactory isEmpty key={`empty-${index}`} />
        ) : (
          <GameCardFactory
            game={game}
            isRtpShown
            key={game.id}
            onDemoClick={() => sendGAEvent(game)}
            onPlayClick={() => sendGAEvent(game)}
          />
        )
      )}
      {link && <ShowAllGameCard isShowAll mode={mode} to={link} />}
    </ScLobbyGamesSlide>
  );
};

function ensureGamesLength(arr: GameV1[], targetLength = 14) {
  const items = arr.slice(0, targetLength);
  return [
    ...items,
    ...Array(targetLength - items.length).fill({ isEmpty: true }),
  ];
}

export const HotAndCold: FC<HotAndColdLobbyGamesSliderProps> = ({
  sliderParams,
  titleIcon,
  title,
  description,
  titleMultiRow,
  blackShowAllCard,
  gameColorMode,
  darkArrows,
  ...props
}) => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();
  const { currentMediaType } = useResponsive();
  const { generateLink } = useNavigation();
  const {
    cols,
    rows,
    allGames,
    slidesPerView = 1,
  } = useMemo(
    () => getSliderParams(sliderParams, currentMediaType),
    [currentMediaType, sliderParams]
  );
  const { hotGames, coldGames, isLoading } = useHotAndColdGames();
  const games = [...hotGames, ...coldGames];

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const seeAllLink = useMemo(() => {
    const link = `/games/${Category.HOT_AND_COLD}#gamesSection`;

    return generateLink(link);
  }, [generateLink]);

  const targetLength = useMemo(() => {
    if (isMobileDevice) return 12;
    return 14;
  }, [isMobileDevice]);

  const ensuredHotGames = ensureGamesLength(hotGames, targetLength);
  const ensuredColdGames = ensureGamesLength(coldGames, targetLength);

  const slides: ISlideProps[] = useMemo(() => {
    const sliderGames = sortHotAndColdLobby(
      ensuredHotGames,
      ensuredColdGames,
      cols
    );
    const chunkSize = rows * cols;

    return Array.from(
      { length: Math.ceil(sliderGames.length / chunkSize) },
      (_, i) => {
        const slideGames = sliderGames.slice(
          i * chunkSize,
          i * chunkSize + chunkSize
        );

        const commonSlideProps = {
          games: slideGames,
          rows,
          cols,
          plugs: createItems({}, cols * rows - slideGames.length),
          blackShowAllCard,
          gameColorMode,
          ...props,
        };

        return commonSlideProps;
      }
    );
  }, [
    hotGames,
    coldGames,
    allGames,
    rows,
    cols,
    props,
    seeAllLink,
    blackShowAllCard,
    gameColorMode,
  ]);

  const totalGames = useMemo(
    () =>
      slides.reduce(
        (total, slide) =>
          total +
          slide.games.filter((game) => !('isEmpty' in game && game.isEmpty))
            .length,
        0
      ),
    [slides]
  );

  if (!(games.length || isLoading))
    return <GamesSkeleton cols={cols} rows={rows} withTitle />;

  return isLoading ? (
    <GamesSkeleton cols={cols} rows={rows} withTitle />
  ) : (
    <NativeSlider<ISlideProps>
      arrows={{
        type: SliderArrow.OUTSIDE,
        dark: darkArrows,
        componentLeft: (
          <SectionTitle multiRow={titleMultiRow}>
            <span className="title">
              {title}
              {titleIcon && <span className="after-title">{titleIcon}</span>}
            </span>
            {description && <span className="description">{description}</span>}
          </SectionTitle>
        ),
        componentRight: totalGames ? (
          <Button
            autoPaddingHorizontal
            className="all-btn"
            id="gameCategoryButton"
            paddingX={8}
            to={seeAllLink}
            whiteTransparent
            xSmall
          >
            {`${t('gameCategories.all')} ${totalGames}`}
          </Button>
        ) : null,
      }}
      slide={Slide}
      slides={slides}
      slidesPerView={slidesPerView}
    />
  );
};
